import React from 'react';
import { motion } from 'framer-motion';
import { AIDentistAnalysis, AIDentistCategoryAnalysis } from '../types';

import ActivityIcon from './icons/ActivityIcon';
import ChevronsLeftRightIcon from './icons/ChevronsLeftRightIcon';
import ToothIcon from './icons/ToothIcon';
import WavesIcon from './icons/WavesIcon';
import SparklesIcon from './icons/SparklesIcon';

interface AIDentistPanelProps {
  analysis: AIDentistAnalysis;
}

const categoryIcons: { [key: string]: React.ReactNode } = {
  'Alignment & Spacing': <ChevronsLeftRightIcon className="w-5 h-5" />,
  'Tooth Condition & Morphology': <ToothIcon className="w-5 h-5" />,
  'Gingival Health': <WavesIcon className="w-5 h-5" />,
  'Overall Aesthetics': <SparklesIcon className="w-5 h-5" />,
};

const severityColors: { [key: string]: { bg: string; text: string; border: string } } = {
  Low: { bg: 'bg-teal-500/10', text: 'text-teal-300', border: 'border-teal-500/20' },
  Moderate: { bg: 'bg-amber-500/10', text: 'text-amber-300', border: 'border-amber-500/20' },
  High: { bg: 'bg-red-500/10', text: 'text-red-300', border: 'border-red-500/20' },
};

const AnalysisCard: React.FC<{ item: AIDentistCategoryAnalysis; index: number }> = ({ item, index }) => {
  const icon = categoryIcons[item.category] || <ActivityIcon className="w-5 h-5" />;

  return (
    <motion.div
      className="bg-zinc-800/30 backdrop-blur-xl border border-white/10 rounded-xl p-4 overflow-hidden shadow-2xl shadow-black/30"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 + 0.3, ease: 'easeOut' }}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-zinc-700/50 flex items-center justify-center text-teal-400">
          {icon}
        </div>
        <div>
          <h4 className="font-bold text-base text-zinc-100">{item.category}</h4>
          <p className="text-xs text-zinc-400">{item.summary}</p>
        </div>
      </div>
      <ul className="mt-4 space-y-2 pl-1">
        {item.findings.map((finding, findIndex) => {
          const colors = severityColors[finding.severity] || severityColors.Low;
          return (
            <li key={findIndex} className="flex items-start gap-3 text-sm">
              <div className={`mt-1 flex-shrink-0 w-2 h-2 rounded-full ${colors.bg} border ${colors.border}`} />
              <p className="text-zinc-300">
                {finding.observation}
                <span className={`ml-2 text-xs font-semibold ${colors.text}`}>({finding.severity})</span>
              </p>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
};

const AIDentistPanel: React.FC<AIDentistPanelProps> = ({ analysis }) => {
  return (
    <motion.aside
      className="w-96 flex-shrink-0 bg-zinc-950/50 backdrop-blur-2xl border-r border-white/10 flex flex-col"
      initial={{ x: '-100%', opacity: 0 }}
      animate={{ x: '0%', opacity: 1 }}
      exit={{ x: '-100%', opacity: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="p-6 border-b border-white/10">
        <motion.h2 
            className="text-2xl font-bold text-white flex items-center gap-3"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
        >
          <ActivityIcon className="w-6 h-6 text-teal-400" />
          <span>AI Dentist Analysis</span>
        </motion.h2>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {analysis.analysis.map((item, index) => (
          <AnalysisCard key={item.category} item={item} index={index} />
        ))}
      </div>
       <footer className="p-4 text-center text-zinc-600 text-xs border-t border-white/10">
            This is an AI-generated analysis for educational purposes and is not a substitute for a professional clinical diagnosis.
       </footer>
    </motion.aside>
  );
};

export default AIDentistPanel;
